import { BALANCES_V2_BLOTTER_PREFIX } from '@src/containers/Blotters/BalancesV2/tokens';
import { CREDIT_BLOTTER_PREFIX } from '@src/containers/Blotters/Credit/tokens';
import { PERSISTED_TABS_ID_PREFIX as HEDGE_POSITION_STATUS_BLOTTER_PREFIX } from '@src/containers/Blotters/HedgePositionStatus/tokens';
import { LOAN_BLOTTER_PREFIX } from '@src/containers/Blotters/OTC/Loans/tokens';
import { OTC_BLOTTER_PREFIX } from '@src/containers/Blotters/OTC/OTCDerivatives/tokens';
import { ORDERS_BLOTTER_PREFIX } from '@src/containers/Blotters/Orders/tokens';
import { POSITIONS_V3_BLOTTER_PREFIX } from '@src/containers/Blotters/PositionsV3/tokens';
import { QUOTES_V2_BLOTTER_PREFIX } from '@src/containers/Blotters/QuotesV2/tokens';
import { RECON_MISMATCHES_BLOTTER_PREFIX } from '@src/containers/Blotters/Reconciliation/tokens';
import { PERSISTED_TABS_ID as TRADE_ALLOCATIONS_BLOTTER_PREFIX } from '@src/containers/Blotters/TradeAllocations/tokens';
import { TRADES_BLOTTER_PREFIX } from '@src/containers/Blotters/Trades/tokens';
import { TRANSFERS_BLOTTER_PREFIX } from '@src/containers/Blotters/Transfers/tokens';
import type { TabProps } from '@talos/kyoko';
import { cloneDeep } from 'lodash-es';
import type { AppConfigState } from '../AppConfigProvider.types';
import {
  BLOTTER_STATE_PANEL_PARAMS_MIGRATION_VERSION,
  shouldRunBlotterStatePanelMigration,
} from '../blotterStatePanelMigrationVersion';

/**
 * Copies legacy blotter table state from app-wide `blotters3` into trading blotter
 * panel `params.blotterTablesState`, keyed by full blotter id.
 *
 * This migration is intentionally copy-only: legacy `blotters3` remains untouched.
 */
export function migrateBlotterStateToPanelParams(initialConfig: unknown) {
  const config = initialConfig as AppConfigState;
  if (!shouldRunBlotterStatePanelMigration(config)) {
    return;
  }

  const legacyBlotterState = config.blotters3;
  if (legacyBlotterState == null) {
    config.blotterStatePanelMigrationVersion = BLOTTER_STATE_PANEL_PARAMS_MIGRATION_VERSION;
    return;
  }

  for (const spaceConfig of config.spacesConfigs?.trading?.layouts ?? []) {
    const panels = spaceConfig?.dockViewLayout?.panels;
    if (panels == null) {
      continue;
    }

    for (const panel of Object.values(panels)) {
      if (panel == null) {
        continue;
      }
      const panelParams = panel.params as
        | {
            type?: string;
            tabs?: unknown;
            blotterTablesState?: Record<string, AppConfigState['blotters3'][string]>;
          }
        | undefined;
      const panelType = String(panelParams?.type ?? panel.contentComponent ?? '');
      const blotterPrefixes = PANEL_TYPE_TO_BLOTTER_PREFIXES[panelType];
      if (!Array.isArray(blotterPrefixes) || blotterPrefixes.length === 0) {
        continue;
      }

      const tabs = panelParams?.tabs as { items?: TabProps[] } | undefined;
      const tabItems = tabs?.items;
      const blotterIDs = getLegacyBlotterIDsForPanel({ blotterPrefixes, tabItems, legacyBlotterState });
      if (blotterIDs.length === 0) {
        continue;
      }

      const currentPanelStateMap = panelParams?.blotterTablesState ?? {};
      const nextPanelStateMap = { ...currentPanelStateMap };
      let changed = false;

      for (const blotterID of blotterIDs) {
        if (nextPanelStateMap[blotterID] != null) {
          continue;
        }

        nextPanelStateMap[blotterID] = cloneDeep(legacyBlotterState[blotterID]);
        changed = true;
      }

      if (changed) {
        const nextPanelParams = {
          ...(panelParams ?? {}),
          type: panelType,
          blotterTablesState: nextPanelStateMap,
        };
        panel.params = nextPanelParams as typeof panel.params;
      }
    }
  }

  config.blotterStatePanelMigrationVersion = BLOTTER_STATE_PANEL_PARAMS_MIGRATION_VERSION;
}

const PANEL_TYPE_TO_BLOTTER_PREFIXES: Record<string, string[]> = {
  allocations: [TRADE_ALLOCATIONS_BLOTTER_PREFIX],
  balancesBlotter: [BALANCES_V2_BLOTTER_PREFIX],
  creditBlotter: [CREDIT_BLOTTER_PREFIX],
  customerBalancesBlotter: ['dealer/customer-balances'],
  customerOrdersBlotter: ['dealer/customer-orders'],
  customerTradesBlotter: ['dealer/customer-trades'],
  customerTransfersBlotter: ['dealer/customer-transactions'],
  hedgePositionStatus: [HEDGE_POSITION_STATUS_BLOTTER_PREFIX],
  loans: [LOAN_BLOTTER_PREFIX],
  ordersBlotter: [ORDERS_BLOTTER_PREFIX],
  otcDerivatives: [OTC_BLOTTER_PREFIX],
  positionsBlotter: [POSITIONS_V3_BLOTTER_PREFIX],
  reconMismatches: [RECON_MISMATCHES_BLOTTER_PREFIX],
  rfqsBlotter: ['principal/quotes', QUOTES_V2_BLOTTER_PREFIX],
  tradesBlotter: [TRADES_BLOTTER_PREFIX],
  transfersBlotter: [TRANSFERS_BLOTTER_PREFIX],
};

function getLegacyBlotterIDsForPanel({
  blotterPrefixes,
  tabItems,
  legacyBlotterState,
}: {
  blotterPrefixes: string[];
  tabItems?: TabProps[];
  legacyBlotterState: AppConfigState['blotters3'];
}): string[] {
  const blotterIDs = new Set<string>();

  if (Array.isArray(tabItems) && tabItems.length > 0) {
    for (const tab of tabItems) {
      if (tab?.id == null) {
        continue;
      }

      for (const blotterPrefix of blotterPrefixes) {
        const blotterID = `${blotterPrefix}/${tab.id}`;
        if (legacyBlotterState[blotterID] == null) {
          continue;
        }

        blotterIDs.add(blotterID);
        break;
      }
    }

    return [...blotterIDs];
  }

  for (const blotterID of Object.keys(legacyBlotterState)) {
    if (blotterPrefixes.some(blotterPrefix => blotterID.startsWith(`${blotterPrefix}/`))) {
      blotterIDs.add(blotterID);
    }
  }

  return [...blotterIDs];
}
